// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - BETA CONFIG
// Centralized beta launch state management
// Source of truth: constants.js BETA_LAUNCH_CONFIG (code defaults)
// Runtime override: localStorage 'distil_admin_overrides' (admin panel)
// ═══════════════════════════════════════════════════════════════════════════

import { BETA_LAUNCH_CONFIG } from './constants';
export { BETA_LAUNCH_CONFIG }; // re-export so App.jsx can import from betaConfig

const ADMIN_OVERRIDE_KEY = 'distil_admin_overrides';

// ─── Read admin overrides from localStorage ────────────────────────────────
function getAdminOverrides() {
  try {
    const raw = localStorage.getItem(ADMIN_OVERRIDE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// ─── Save admin overrides to localStorage ─────────────────────────────────
function saveAdminOverrides(overrides) {
  localStorage.setItem(ADMIN_OVERRIDE_KEY, JSON.stringify(overrides));
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if TGE tracking is enabled (beta launched + rewards not paused)
 * Used in App.jsx to gate trackPurificationForTGE calls
 */
export function isTrackingEnabled() {
  const status = getBetaStatus();
  return status.isLaunched && !status.rewardsPaused;
}

/**
 * Get full beta status (merges code config with admin overrides)
 */
export function getBetaStatus() {
  const overrides = getAdminOverrides();

  const isLaunched = overrides.isLaunched !== undefined
    ? overrides.isLaunched
    : BETA_LAUNCH_CONFIG.isLaunched;

  const phase = overrides.phase || BETA_LAUNCH_CONFIG.phase;

  const rewardsPaused = overrides.rewardsPaused || false;

  const multiplier = rewardsPaused ? 0 : (BETA_LAUNCH_CONFIG.multipliers[phase] || 1.0);

  return {
    isLaunched,
    phase,
    rewardsPaused,
    multiplier,
    trackingEnabled: true,
    launchDate: overrides.launchDate || BETA_LAUNCH_CONFIG.startDate,
    hasOverrides: Object.keys(overrides).length > 0,
  };
}

/**
 * Get current phase info
 */
export function getCurrentPhaseInfo() {
  const status = getBetaStatus();
  return {
    phase: status.phase,
    multiplier: status.multiplier,
    isLaunched: status.isLaunched,
    rewardsPaused: status.rewardsPaused,
  };
}

/**
 * Get current multiplier (respects kill switch)
 */
export function getCurrentMultiplier() {
  const status = getBetaStatus();
  if (status.rewardsPaused) return 0;
  if (!status.isLaunched) return 1.0;
  return status.multiplier;
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN CONTROLS (only callable from admin panel, no effect on regular users)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Toggle beta launch on/off
 */
export function setLaunched(value) {
  const overrides = getAdminOverrides();
  overrides.isLaunched = value;
  if (value) overrides.launchDate = new Date().toISOString();
  saveAdminOverrides(overrides);
}

/**
 * Set beta phase
 */
export function setPhase(phase) {
  const overrides = getAdminOverrides();
  overrides.phase = phase;
  saveAdminOverrides(overrides);
}

/**
 * Kill switch - pause ALL reward calculations
 */
export function setRewardsPaused(value) {
  const overrides = getAdminOverrides();
  overrides.rewardsPaused = value;
  saveAdminOverrides(overrides);
}

/**
 * Clear all admin overrides (revert to code defaults)
 */
export function clearAdminOverrides() {
  localStorage.removeItem(ADMIN_OVERRIDE_KEY);
}

export default {
  isTrackingEnabled,
  getBetaStatus,
  getCurrentPhaseInfo,
  getCurrentMultiplier,
  setLaunched,
  setPhase,
  setRewardsPaused,
  clearAdminOverrides,
  BETA_LAUNCH_CONFIG,
};
